import React, { FC, useCallback, useEffect } from 'react';
import { FormInstance, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import produce from 'immer';
import styled from 'styled-components';

import {
  Checkbox,
  DateInput,
  Form,
  DocumentInput,
  Select,
  NationalitySelect,
  RelationSelect,
  IdTypeSelect,
  PhoneNumberFormItem,
} from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import EmailFormItem from '~/components/inputs/EmailFormItem';
import theme from '~/constants/theme';
import i18n from '~/i18n';
import CustomFields from '~/components/CustomFields';
import { NamePath } from '~/utils/buildNamePath';
import WithPep from '~/components/WithPep';
import { useHandbook } from '~/controllers/handbook';

interface Props {
  name: string | (string | number)[];
  form: FormInstance;
  isSignedWithEmail: boolean;
  hasVerifiedPhoneNumber: boolean;
  isCheckedAuthorizedSignatory?: boolean;
  isSelectedCompanyLicense?: boolean;
  hasPassport?: boolean;
  personalDocumentType?: string;
  withCustomFields?: boolean;
}

const PersonFormItems: FC<Props> = (props) => {
  const {
    name,
    form,
    isSignedWithEmail,
    hasVerifiedPhoneNumber,
    isCheckedAuthorizedSignatory,
    isSelectedCompanyLicense,
    hasPassport,
    personalDocumentType,
    withCustomFields = true,
  } = props;
  const { t } = useTranslation('ksaApplication');
  const handbook = useHandbook();
  const possibleDocumentsForAuthorizedSignatory = handbook.data?.handbooks
    ?.find((item) => item.key === 'idType')
    ?.values?.filter((item) => item.key === 'idType.IQAMA' || item.key === 'idType.NID')
    ?.map((item) => [item.localizedValue, item.apiValue])
    .flat();

  useEffect(() => {
    setTimeout(() => {
      const formValues = form.getFieldsValue();
      const initialPerson: KsaPerson = get(formValues, name) || {};
      const patchedPerson = produce<KsaPerson>(initialPerson, (draft) => {
        if (!draft.personalData?.isAuthorizedSignatory) {
          draft.personalData.isAuthorizedSignatory = !!isCheckedAuthorizedSignatory;
        }
        if (isSelectedCompanyLicense && !draft.personalData?.proof?.documentType) {
          draft.personalData.proof = {};
          draft.personalData.proof.documentType = listedOnCompanyLicense;
        }
        if (!draft.personalData?.isPep) draft.personalData.isPep = false;
      });
      form.setFields([{ name, value: patchedPerson }]);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPersonalDocument = !!personalDocumentType;
  const canBeAuthorizedRepresentative =
    !!possibleDocumentsForAuthorizedSignatory?.includes(personalDocumentType);
  const n = useCallback((namePath: NamePath) => [name].flat().concat([namePath].flat()), [name]);

  return (
    <Container>
      <Form.Item name={n(commonNames.uid)} hidden />
      <Form.Item name={n(['personalData', 'email'])} hidden />
      <Form.Item name={n(['personalData', 'phoneNumber'])} hidden />
      <Section formItemFirst formItemLast>
        <Typography.Title level={4}>{t('Personal data').toUpperCase()}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item label={t('Full name')} name={n(commonNames.fullName)} disabled>
            <Input />
          </Form.Item>
          {/* <Form.Item label={t('Date of birth')} name={n(commonNames.dateOfBirth)} disabled>
            <DateInput disableDateRule="date <= today" />
          </Form.Item> */}
          <Form.Item label={t('Nationality')} name={n(commonNames.nationality)} disabled>
            <NationalitySelect />
          </Form.Item>
          <Form.Item label={t('Relation')} name={n(commonNames.relation)} disabled>
            <RelationSelect />
          </Form.Item>
        </InputsGroupLayout>
        <Form.Item
          name={n(['personalData', 'isPep'])}
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox>
            <WithPep>{t('This person is a Politically Exposed Person (PEP)')}</WithPep>
          </Checkbox>
        </Form.Item>
        {canBeAuthorizedRepresentative ? (
          <Form.Item
            name={n(['personalData', 'isAuthorizedSignatory'])}
            valuePropName="checked"
            disabled={isCheckedAuthorizedSignatory}
          >
            <Checkbox>{t('This person is authorised signatory')}</Checkbox>
          </Form.Item>
        ) : null}
        <InputsGroupLayout>
          <Form.Visible name={n(['personalData', 'isAuthorizedSignatory'])}>
            <EmailFormItem name={n(['personalData', 'email'])} disabled={isSignedWithEmail} />
          </Form.Visible>
          <Form.Visible name={n(['personalData', 'isAuthorizedSignatory'])}>
            <PhoneNumberFormItem
              name={n(['personalData', 'phoneNumber'])}
              disabled={hasVerifiedPhoneNumber}
            />
          </Form.Visible>
        </InputsGroupLayout>
      </Section>

      <Form.Visible name={n(['personalData', 'isAuthorizedSignatory'])}>
        <Section>
          <Typography.Title level={4}>
            {t('Authorised Signatory Proof').toUpperCase()}
          </Typography.Title>
          <Form.Item
            label={t('Select the document type')}
            name={n(['personalData', 'proof', 'documentType'])}
          >
            <Select
              className="AuthorizedRepresentativeFormItems-DocumentTypeSelect"
              options={getProofOptions()}
              disabled={isSelectedCompanyLicense}
            />
          </Form.Item>
          <Form.Visible
            name={n(['personalData', 'proof', 'documentType'])}
            visibleIf={uploadPowerOfAttorney}
          >
            <Form.Item name={n(['personalData', 'proof', 'files'])}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item>
          </Form.Visible>
        </Section>
      </Form.Visible>

      <Section>
        <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>
        <Typography.Title level={5}>{t('Passport data:')}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item
            label={t('Passport number')}
            name={n(['passport', 'documentNumber'])}
            disabled={hasPassport}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('Passport issue date')} name={n(['passport', 'issueDate'])}>
            <DateInput disableDateRule="date <= today" allowClear />
          </Form.Item>
          <Form.Item label={t('Passport expiry date')} name={n(['passport', 'expiryDate'])}>
            <DateInput disableDateRule="date > today" allowClear />
          </Form.Item>
        </InputsGroupLayout>
      </Section>

      <Section formItemLast>
        <Typography.Title level={4}>{t('Personal Document').toUpperCase()}</Typography.Title>
        <Typography.Title level={5}>{t('Document data:')}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item
            label={t('ID type')}
            name={n(commonNames.idType)}
            disabled={hasPersonalDocument}
          >
            <IdTypeSelect />
          </Form.Item>
          <Form.Item
            label={t('ID number')}
            name={n(commonNames.documentNumber)}
            disabled={hasPersonalDocument}
          >
            <Input />
          </Form.Item>
        </InputsGroupLayout>
      </Section>

      {!!withCustomFields && <CustomFields nFn={n} />}
    </Container>
  );
};

export const commonNames = {
  uid: ['uid'],
  fullName: ['personalData', 'fullName'],
  dateOfBirth: ['personalData', 'dateOfBirth'],
  nationality: ['personalData', 'nationality'],
  relation: ['personalData', 'relation'],
  idType: ['personalDocument', 'idType'],
  documentNumber: ['personalDocument', 'idNumber'],
};

export const uploadPowerOfAttorney =
  'Upload a copy of the Power of Attorney showing this person as an authorised signatory';
const listedOnCompanyLicense =
  'This person is listed on the Company License as part of the company ownership structure';
const getProofOptions = () => [
  {
    label: i18n.t(listedOnCompanyLicense, { ns: 'ksaApplication' }),
    value: listedOnCompanyLicense,
  },
  {
    label: i18n.t(uploadPowerOfAttorney, { ns: 'ksaApplication' }),
    value: uploadPowerOfAttorney,
  },
];

const Container = styled.div`
  @media (max-width: ${theme.breakpoints.md}px) {
    .AuthorizedRepresentativeFormItems-DocumentTypeSelect {
      max-width: calc(100vw - ${32 * 2}px);
    }
  }
  @media (max-width: ${theme.breakpoints.sm}px) {
    .AuthorizedRepresentativeFormItems-DocumentTypeSelect {
      max-width: calc(100vw - ${12 * 2 + 12 * 2}px);
    }
  }
`;

export default PersonFormItems;
